import { ApiError, errorMessage } from "@lumora/bridge";
import { ViewModel } from "../mvvm/ViewModel.js";
/** Wrong codes in a row before the View suggests starting over (the mfaToken may have expired, the API never says). */
export const MFA_START_OVER_AFTER = 3;
/**
 * Second step of an MFA sign-in (#1501): trades the `mfaToken` from the login step plus a TOTP
 * code or a recovery code for the session. The API answers every failure with 401
 * invalid_credentials (wrong, replayed, expired token, locked), so repeated misses map to "start over".
 */
export class MfaChallengeViewModel extends ViewModel {
    client;
    mfaToken;
    onSession;
    constructor(client, mfaToken, onSession) {
        super({ factor: "code", code: "", recoveryCode: "", loading: false, error: null, failures: 0, startOver: false, session: null });
        this.client = client;
        this.mfaToken = mfaToken;
        this.onSession = onSession;
    }
    setFactor(factor) {
        this.setState({ factor, error: null });
    }
    /** Keeps digits only, 6 max (authenticator apps show "123 456"). */
    setCode(raw) {
        this.setState({ code: raw.replace(/\D/g, "").slice(0, 6), error: null });
    }
    setRecoveryCode(raw) {
        this.setState({ recoveryCode: raw.trim().slice(0, 32), error: null });
    }
    /** True when the active field holds a plausible value. */
    get canSubmit() {
        const s = this.state;
        if (s.loading || s.startOver)
            return false;
        return s.factor === "code" ? s.code.length === 6 : s.recoveryCode.replace(/[^a-z0-9]/gi, "").length >= 16;
    }
    async submit() {
        if (!this.canSubmit) {
            if (!this.state.loading && !this.state.startOver) {
                this.setState({
                    error: this.state.factor === "code" ? "Enter the 6-digit code from your authenticator app." : "Enter one of your recovery codes.",
                });
            }
            return false;
        }
        const { factor, code, recoveryCode } = this.state;
        this.setState({ loading: true, error: null });
        try {
            const session = await this.client.mfaChallenge({
                mfaToken: this.mfaToken,
                ...(factor === "code" ? { code } : { recoveryCode }),
            });
            this.setState({ loading: false, session, code: "", recoveryCode: "" });
            this.onSession?.(session);
            return true;
        }
        catch (err) {
            this.setState({ loading: false, code: "", recoveryCode: "", ...this.failure(err) });
            return false;
        }
    }
    failure(err) {
        if (err instanceof ApiError) {
            if (err.status === 429 || err.code === "locked") {
                return { startOver: true, error: "Too many attempts. Wait a few minutes, then sign in again." };
            }
            if (err.status === 503 || err.code === "mfa_unavailable") {
                return { error: "Two-step sign-in is not available right now. Try again shortly." };
            }
            if (err.status === 401) {
                const failures = this.state.failures + 1;
                if (failures >= MFA_START_OVER_AFTER) {
                    return { failures, startOver: true, error: "That did not work and the sign-in may have timed out. Start over to try again." };
                }
                return { failures, error: "That code did not work. Check it and try again." };
            }
        }
        return { error: errorMessage(err, "Could not verify the code. Please try again.") };
    }
}
//# sourceMappingURL=MfaChallengeViewModel.js.map